/**
 * Registro de clientes del Brain — UN archivo por cliente.
 *
 * Hasta el 30/09/2026 la config de un cliente vivía repartida en cuatro listas de
 * código (BRAIN_CLIENTS, SLACK_CHANNEL_CONFIGS, BQ_CLIENT_REGISTRY, CLIENT_FUZZY_TERMS)
 * más las sugerencias del chat: cada PR de onboarding parcheaba las cuatro con
 * marcadores de texto, y las 44 PRs generadas el 9/9 quedaron todas en conflicto
 * entre sí. Ahora un cliente es `shared/brain-clients/<id>.json`, validado con este
 * schema, y `shared/brain-clients/index.ts` es un índice GENERADO
 * (`npm run brain:clients`; `test:brain-clients` en CI falla si está viejo).
 * Las cuatro listas viejas se DERIVAN del índice: nadie las edita a mano.
 *
 * Este módulo es puro (sin I/O): lo usan el generador, el test, el generador de
 * PRs de onboarding (backend) y, a través del índice, backend y frontend.
 */
import { z } from 'zod';

export const BRAIN_CLIENT_ID_RE = /^[a-z0-9][a-z0-9-]{1,39}$/;

/**
 * Lo que puede llevar un nombre de Capacity_Final / Jotform / Hour Allocator para
 * entrar en una consulta de BigQuery (letras con tilde, dígitos, separadores que
 * Finance usa: "BET_X | GOOGLE", "INDEED <> CONTENT", "HELLMANN'S"). El conector
 * escapa con `bqLit`; esto es defensa en profundidad y falla en el form / CI en vez
 * de en el preflight de las 3 de la mañana.
 */
export const BQ_SAFE_NAME_RE = /^[\p{L}\p{N}\s\-.,()&/|<>:_'+\[\]#]+$/u;

const nonEmpty = z.string().trim().min(1);
const bqName = nonEmpty.regex(BQ_SAFE_NAME_RE, 'nombre con caracteres que BigQuery no acepta');
/** Un `\uXXXX` copiado crudo desde un literal TS es un nombre roto, no un escape. */
const noBackslash = (s: string) => !s.includes('\\');

export const brainSlackChannelSchema = z
  .object({
    channelId: z.string().regex(/^[CG][A-Z0-9]{6,}$/, 'channelId de Slack inválido'),
    channelName: nonEmpty.refine(noBackslash, 'channelName con barra invertida (¿escape unicode sin decodificar?)'),
    /** Secreto (Secrets Manager) con el token del bot del workspace EXTERNO del cliente. */
    botTokenSecret: nonEmpty.optional(),
  })
  .strict();

export const brainClientConfigSchema = z
  .object({
    id: z.string().regex(BRAIN_CLIENT_ID_RE, 'id: minúsculas, dígitos y guiones'),
    displayName: z.string().trim().min(2).max(80),
    /** Pseudo-cliente (portfolio): sin Slack/BigQuery/Meet propios; no cuenta en los chequeos por cliente. */
    pseudo: z.boolean().optional(),
    slack: z
      .object({
        channels: z.array(brainSlackChannelSchema).default([]),
        /** El cliente tiene workspace propio: invitar a solange.guerrero@winclap.com antes de indexar. */
        externalWorkspace: z.object({ channelNames: z.array(nonEmpty).default([]) }).strict().optional(),
      })
      .strict()
      .default({ channels: [] }),
    bigquery: z
      .object({
        /** Tal cual aparecen en Capacity_Final.ClientName (TRIM aplicado). */
        capacityNames: z.array(bqName).min(1),
        /** Tal cual en Jotform_Data (client_raw); se compara con UPPER(TRIM()). */
        jotformName: bqName,
        /**
         * Nombres del cliente en la app de Carga de Horas y Assets (BigQuery
         * `neondb_hourAllocator`): `clients.name` y el export legacy `final_table.company_name`
         * son el mismo nombre. Solo hace falta cuando difiere de `capacityNames` / `displayName`:
         * el Org Service resuelve nombre → clientId por capacityNames, allocatorNames, displayName
         * y familia ("X - Y" → "X"), en ese orden (`resolveClientId` de shared/org-rules.ts).
         */
        allocatorNames: z.array(bqName).default([]),
      })
      .strict()
      .optional(),
    meet: z
      .object({
        /** Substrings (en minúsculas) que identifican al cliente en el título de un doc de Meet. */
        fuzzyTerms: z.array(nonEmpty).default([]),
      })
      .strict()
      .default({ fuzzyTerms: [] }),
    hubspot: z
      .object({
        /** Fichas de HubSpot del cliente (madre + hijas + relacionadas). */
        companyIds: z.array(z.number().int().positive()).default([]),
        /** `hitlist_id` de HubSpot (dominio corporativo que agrupa las fichas). */
        hitlistId: nonEmpty.optional(),
      })
      .strict()
      .optional(),
    /** Preguntas sugeridas propias del cliente (el chat las muestra antes de las genéricas). */
    questions: z
      .object({ es: z.array(nonEmpty), en: z.array(nonEmpty), pt: z.array(nonEmpty) })
      .strict()
      .optional(),
  })
  .strict();

export type BrainClientConfig = z.infer<typeof brainClientConfigSchema>;
/** Lo que se escribe en el JSON (los defaults pueden faltar). */
export type BrainClientConfigInput = z.input<typeof brainClientConfigSchema>;

/** Parsea y normaliza un JSON de cliente. Tira con el path del campo que falla. */
export function parseBrainClientConfig(raw: unknown, label = 'cliente'): BrainClientConfig {
  const r = brainClientConfigSchema.safeParse(raw);
  if (!r.success) {
    const issues = r.error.issues.map((i) => `${i.path.join('.') || '(raíz)'}: ${i.message}`).join('; ');
    throw new Error(`${label}: ${issues}`);
  }
  return r.data;
}

/**
 * Reglas ENTRE clientes (las que antes vivían en scripts/audit-brain-bq/audit.mjs):
 * ids únicos, todo cliente real con BigQuery, ningún nombre de Capacity / de Carga de
 * Horas / canal de Slack / ficha de HubSpot compartido por dos clientes, términos de
 * Meet en minúsculas. Devuelve la lista de errores (vacía = OK).
 */
export function validateBrainClientConfigs(configs: BrainClientConfig[]): string[] {
  const errors: string[] = [];
  const seenId = new Set<string>();
  const capacity = new Map<string, string>();
  const allocator = new Map<string, string>();
  const channels = new Map<string, string>();
  const companies = new Map<number, string>();

  for (const c of configs) {
    if (seenId.has(c.id)) errors.push(`id "${c.id}" repetido`);
    seenId.add(c.id);

    if (!c.pseudo && !c.bigquery) {
      errors.push(`${c.id}: sin BigQuery — el Brain contestaría sin revenue ni targets (Nubank/Naranja X, 08/2026)`);
    }
    for (const n of c.bigquery?.capacityNames ?? []) {
      if (/[\n\r`\\]/.test(n)) errors.push(`${c.id}: nombre de Capacity inválido ${JSON.stringify(n)}`);
      const k = n.trim().toUpperCase();
      const other = capacity.get(k);
      if (other && other !== c.id) errors.push(`nombre de Capacity "${n}" repetido entre ${other} y ${c.id} (mezclaría revenue)`);
      capacity.set(k, c.id);
    }
    for (const n of c.bigquery?.allocatorNames ?? []) {
      const k = n.trim().toUpperCase().replace(/\s+/g, ' '); // reuse-ok: misma clave que normClient; importar content-productivity acá invertiría la dependencia de shared/
      const other = allocator.get(k);
      if (other && other !== c.id) errors.push(`nombre de Carga de Horas "${n}" repetido entre ${other} y ${c.id} (mezclaría asignaciones)`);
      allocator.set(k, c.id);
      // Entre listas: el Org Service resuelve un nombre de la app contra allocatorNames, capacityNames y
      // displayName de TODOS los clientes; un nombre de la app de A igual a un nombre de Capacity de B
      // resolvería a A en silencio.
      const inCapacity = capacity.get(k);
      if (inCapacity && inCapacity !== c.id) errors.push(`nombre de Carga de Horas "${n}" de ${c.id} es un nombre de Capacity de ${inCapacity}`);
    }
    for (const ch of c.slack.channels) {
      const other = channels.get(ch.channelId);
      if (other === c.id) errors.push(`${c.id}: canal ${ch.channelId} (#${ch.channelName}) repetido dentro del mismo cliente`);
      else if (other) errors.push(`canal ${ch.channelId} (#${ch.channelName}) repetido entre ${other} y ${c.id}`);
      channels.set(ch.channelId, c.id);
    }
    for (const id of c.hubspot?.companyIds ?? []) {
      const other = companies.get(id);
      if (other && other !== c.id) errors.push(`ficha de HubSpot ${id} repetida entre ${other} y ${c.id}`);
      companies.set(id, c.id);
    }
    for (const t of c.meet.fuzzyTerms) {
      if (t !== t.toLowerCase()) errors.push(`${c.id}: término de Meet "${t}" tiene que estar en minúsculas (el matcher baja el título a minúsculas)`);
    }
    if (c.pseudo && (c.slack.channels.length || c.bigquery || c.meet.fuzzyTerms.length)) {
      errors.push(`${c.id}: un pseudo-cliente no lleva Slack, BigQuery ni Meet`);
    }
  }
  return errors;
}

const LETRA_O_NUMERO = /[\p{L}\p{N}]/u;

/**
 * Posición del término en el texto, pero solo si cae en un LÍMITE DE PALABRA: el
 * carácter de antes y el de después no pueden ser letra ni número.
 *
 * Por qué (censo del 05/10/2026): con `indexOf` pelado, los términos cortos matchean
 * adentro de palabras comunes y archivan reuniones internas como si fueran de un
 * cliente. Verificado con el código real:
 *   "Revisar propuesta"   → visa      (porque "revisar" contiene "visa")
 *   "Amelia / Juan - 1:1" → meli      (porque "amelia" contiene "meli")
 *   "Taxes Q3"            → unilever  (porque "taxes" contiene "axe")
 * Y al revés, sin límites no se pueden cargar siglas reales: "KC" (Kimberly-Clark) o
 * "MLA/MLC/MLU" (mercados de MELI) matchearían dentro de cualquier palabra.
 *
 * Busca TODAS las ocurrencias, no solo la primera: en "Melina y KC" el "meli" de
 * "Melina" no vale, pero si más adelante hubiera un "meli" suelto, sí.
 */
function posicionEnLimiteDePalabra(texto: string, termino: string): number {
  let desde = 0;
  for (;;) {
    const at = texto.indexOf(termino, desde);
    if (at < 0) return -1;
    const antes   = at > 0 ? texto[at - 1]! : '';
    const despues = at + termino.length < texto.length ? texto[at + termino.length]! : '';
    if (!LETRA_O_NUMERO.test(antes) && !LETRA_O_NUMERO.test(despues)) return at;
    desde = at + 1;
  }
}

/**
 * Qué cliente nombra un texto (el título de un doc de Meet), a partir de los
 * términos de cada uno (`meet.fuzzyTerms`, en minúsculas). Regla: gana el cliente
 * que aparece PRIMERO en el texto; a igual posición, el término más largo (el más
 * específico). Antes ganaba el primer cliente en orden de alta en la lista, y con
 * el registro ordenado por id "Sync Latam x Despegar" pasaba de latam a despegar.
 *
 * El término tiene que aparecer como palabra, no como pedazo de otra
 * (`posicionEnLimiteDePalabra`). Los términos con símbolos siguen funcionando:
 * "p&g", "star+" y "l'oréal" matchean porque el símbolo no es letra ni número.
 */
export function matchBrainClientByTerms(text: string, terms: Record<string, readonly string[]>): string | null {
  const lower = text.toLowerCase();
  let best: { id: string; at: number; len: number } | null = null;
  for (const [id, list] of Object.entries(terms)) {
    for (const t of list) {
      if (!t) continue;
      const at = posicionEnLimiteDePalabra(lower, t);
      if (at < 0) continue;
      if (!best || at < best.at || (at === best.at && t.length > best.len)) best = { id, at, len: t.length };
    }
  }
  return best?.id ?? null;
}

/**
 * Cuántas veces menciona el texto a cada cliente (términos como PALABRA, igual que
 * `matchBrainClientByTerms`). Para transcripciones cuyo TÍTULO no nombra a nadie:
 * "Reunión iniciada a las 2026/09/22 13:49" — 1.687 en 120 días según el censo del
 * 05/10/2026. El título no sirve; el contenido sí.
 *
 * Suma las menciones de TODOS los términos del cliente y no cuenta dos veces el mismo
 * tramo de texto: con "mercado libre" y "meli" cargados, "Mercado Libre" es UNA mención.
 *
 * Pura, sin reloj ni red, para poder testearla.
 */
export function contarMencionesPorCliente(
  text: string,
  terms: Record<string, readonly string[]>
): Record<string, number> {
  const lower = text.toLowerCase();
  const out: Record<string, number> = {};
  for (const [id, list] of Object.entries(terms)) {
    const tramos: Array<[number, number]> = [];
    for (const t of list) {
      if (!t) continue;
      let desde = 0;
      for (;;) {
        const at = posicionEnLimiteDePalabra(lower.slice(desde), t);
        if (at < 0) break;
        const abs = desde + at;
        // Un término más largo que ya cubrió este tramo no se cuenta de nuevo.
        if (!tramos.some(([a, b]) => abs >= a && abs < b)) tramos.push([abs, abs + t.length]);
        desde = abs + 1;
      }
    }
    if (tramos.length) out[id] = tramos.length;
  }
  return out;
}

export interface AtribucionPorMenciones {
  /** Cliente resuelto sin ayuda de ningún modelo, o null. */
  clientId: string | null;
  /** Clientes con evidencia suficiente. Con 2 o más hay que desempatar. */
  candidatos: string[];
  motivo: 'sin-menciones' | 'sin-evidencia-suficiente' | 'unico' | 'empate';
}

/**
 * Mínimo de menciones para considerar que una transcripción ES de un cliente y no que
 * lo nombró al pasar. Un weekly interno dice "Rappi" una vez; una reunión DE Rappi lo
 * dice todo el tiempo.
 */
export const MIN_MENCIONES_PARA_ATRIBUIR = 3;

/**
 * Qué hacer con las menciones encontradas. Separada de la cuenta y del modelo a
 * propósito: así la decisión se testea sin red, y el modelo solo se usa para DESEMPATAR
 * entre candidatos que ya tienen evidencia en el texto — nunca para elegir libremente.
 */
export function decidirAtribucionPorMenciones(
  menciones: Record<string, number>,
  minMenciones = MIN_MENCIONES_PARA_ATRIBUIR
): AtribucionPorMenciones {
  const entradas = Object.entries(menciones);
  if (!entradas.length) return { clientId: null, candidatos: [], motivo: 'sin-menciones' };
  const conEvidencia = entradas
    .filter(([, n]) => n >= minMenciones)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  if (!conEvidencia.length) return { clientId: null, candidatos: [], motivo: 'sin-evidencia-suficiente' };
  if (conEvidencia.length === 1) return { clientId: conEvidencia[0]![0], candidatos: [conEvidencia[0]![0]], motivo: 'unico' };
  // Si uno domina con holgura (el doble que el segundo), no hace falta preguntarle a nadie.
  const [primero, segundo] = conEvidencia;
  if (primero![1] >= segundo![1] * 2) {
    return { clientId: primero![0], candidatos: conEvidencia.map(([id]) => id), motivo: 'unico' };
  }
  return { clientId: null, candidatos: conEvidencia.map(([id]) => id), motivo: 'empate' };
}

export const BRAIN_CLIENTS_DIR = 'shared/brain-clients';
export const BRAIN_CLIENTS_INDEX_PATH = `${BRAIN_CLIENTS_DIR}/index.ts`;
/** Vista liviana (id, nombre, preguntas): lo ÚNICO del registro que entra al bundle del frontend. */
export const BRAIN_CLIENTS_PUBLIC_PATH = `${BRAIN_CLIENTS_DIR}/public.ts`;

export interface BrainClientPublic {
  id: string;
  displayName: string;
  pseudo?: boolean;
  questions?: BrainClientConfig['questions'];
}

export function toBrainClientPublic(c: BrainClientConfig): BrainClientPublic {
  return {
    id: c.id,
    displayName: c.displayName,
    ...(c.pseudo ? { pseudo: true } : {}),
    ...(c.questions ? { questions: c.questions } : {}),
  };
}

/**
 * El índice público: sin canales, nombres de secretos ni nombres de Capacity. El
 * JS de Vite se sirve antes del login, así que lo que entre acá lo ve cualquiera.
 */
export function renderBrainClientsPublic(configs: BrainClientConfig[]): string {
  const sorted = [...configs].sort((a, b) => a.id.localeCompare(b.id)).map(toBrainClientPublic);
  const body = sorted
    .map((c) => `  ${JSON.stringify(c.id)}: ${JSON.stringify(c, null, 2).replace(/\n/g, '\n  ')},`)
    .join('\n');
  return (
    '// GENERADO por `npm run brain:clients` a partir de shared/brain-clients/*.json — NO editar a mano.\n' +
    '// Vista PÚBLICA del registro (id, nombre, preguntas sugeridas): es lo único que importa el frontend.\n' +
    '// Lo demás (canales, secretos, BigQuery, HubSpot) vive en index.ts y NO entra al bundle del front.\n' +
    "import type { BrainClientPublic } from '../brain-client-config';\n\n" +
    `export const BRAIN_CLIENT_PUBLIC: Readonly<Record<string, BrainClientPublic>> = {\n${body}\n};\n\n` +
    'export const BRAIN_CLIENT_IDS: readonly string[] = Object.keys(BRAIN_CLIENT_PUBLIC);\n'
  );
}

/** Ruta del JSON de un cliente dentro del repo. */
export function brainClientJsonPath(clientId: string): string {
  return `${BRAIN_CLIENTS_DIR}/${clientId}.json`;
}

/** Cómo se escribe un JSON de cliente (orden de claves fijo, 2 espacios, salto final). */
export function renderBrainClientJson(config: BrainClientConfigInput): string {
  const src = config as Record<string, unknown>;
  const ordered: Record<string, unknown> = {};
  for (const k of ['id', 'displayName', 'pseudo', 'slack', 'bigquery', 'meet', 'hubspot', 'questions']) {
    if (src[k] !== undefined) ordered[k] = src[k];
  }
  return JSON.stringify(ordered, null, 2) + '\n';
}

/**
 * El índice generado: los configs ya normalizados (defaults aplicados), ordenados
 * por id, como una constante tipada. Determinista: misma entrada, mismo archivo.
 */
export function renderBrainClientsIndex(configs: BrainClientConfig[]): string {
  const sorted = [...configs].sort((a, b) => a.id.localeCompare(b.id));
  const body = sorted
    .map((c) => `  ${JSON.stringify(c.id)}: ${JSON.stringify(c, null, 2).replace(/\n/g, '\n  ')},`)
    .join('\n');
  return (
    '// GENERADO por `npm run brain:clients` a partir de shared/brain-clients/*.json — NO editar a mano.\n' +
    '// Un cliente nuevo = un JSON nuevo + regenerar este índice. `test:brain-clients` (CI) falla si está viejo.\n' +
    '// SOLO backend y scripts: trae canales, nombres de secretos y de Capacity. El front importa public.ts.\n' +
    "import type { BrainClientConfig } from '../brain-client-config';\n\n" +
    `export const BRAIN_CLIENT_REGISTRY: Readonly<Record<string, BrainClientConfig>> = {\n${body}\n};\n`
  );
}
